// Enhanced src/components/math/MathText.jsx
import React, { useEffect, useRef, useState } from 'react';
import { MathUtils } from './MathUtils';

export const MathText = ({ 
  children, 
  className = '', 
  inline = true,
  style = {} 
}) => {
  const containerRef = useRef(null);
  const [processedContent, setProcessedContent] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [renderKey, setRenderKey] = useState(0);

  useEffect(() => {
    const processText = async () => {
      if (!children || typeof children !== 'string') {
        setProcessedContent(children || '');
        return;
      }

      setIsProcessing(true);
      setError(null);

      try {
        console.log('MathText processing:', children);
        
        // Check if the text contains math
        if (MathUtils.hasMathContent(children)) {
          console.log('Math content detected in:', children);
          
          // Normalize all delimiter types for consistent processing
          let processed = children.trim();
          
          // Keep dollar signs as-is since MathJax now supports them
          // Just ensure proper spacing around inline math
          processed = processed.replace(/\$([^$]+)\$/g, ' $$$1$$ ').replace(/\s+/g, ' ').trim();
          
          console.log('Normalized content:', processed);
          
          // Process with markdown (math is protected)
          const withMarkdown = await MathUtils.processContentWithMarkdown(processed);
          setProcessedContent(withMarkdown);
          
          // Trigger re-render
          setRenderKey(prev => prev + 1);
          
        } else {
          // No math content, process as regular markdown
          const withMarkdown = await MathUtils.processContentWithMarkdown(children);
          setProcessedContent(withMarkdown);
        }
      } catch (error) {
        console.error('Error processing math text:', error);
        setError(error.message);
        setProcessedContent(children);
      } finally {
        setIsProcessing(false);
      }
    };

    processText();
  }, [children]);

  // Force math re-rendering
  useEffect(() => {
    const renderMath = async () => {
      if (processedContent && containerRef.current && !isProcessing && !error) {
        try {
          if (MathUtils.hasMathContent(children)) {
            console.log('Re-rendering math for:', children);
            // Small delay to ensure DOM is ready
            await new Promise(resolve => setTimeout(resolve, 50));
            await MathUtils.renderMath(containerRef.current);
            console.log('Math re-rendered successfully');
          }
        } catch (error) {
          console.error('Error re-rendering math:', error);
          setError('Math re-rendering failed');
        }
      }
    };

    renderMath();
  }, [processedContent, isProcessing, error, children, renderKey]);

  // Handle re-renders when component updates
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      const hasRelevantChanges = mutations.some(mutation => 
        mutation.type === 'childList' || 
        (mutation.type === 'attributes' && mutation.attributeName === 'class')
      );
      
      if (hasRelevantChanges && containerRef.current && MathUtils.hasMathContent(children)) {
        console.log('DOM mutation detected, re-rendering math');
        setTimeout(async () => {
          try {
            await MathUtils.renderMath(containerRef.current);
          } catch (error) {
            console.error('Error in mutation observer math render:', error);
          }
        }, 100);
      }
    });

    if (containerRef.current?.parentElement) {
      observer.observe(containerRef.current.parentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }

    return () => observer.disconnect();
  }, [children]);

  const containerProps = {
    ref: containerRef,
    className: `math-text ${className} ${error ? 'math-error' : ''}`,
    style: {
      display: inline ? 'inline-block' : 'block',
      verticalAlign: inline ? 'middle' : 'baseline',
      lineHeight: inline ? '1.4' : '1.6',
      minHeight: inline ? '1.2em' : 'auto',
      ...style
    },
    key: `math-${renderKey}`
  };

  if (error) {
    return (
      <span {...containerProps} title={`Math error: ${error}`}>
        {children}
      </span>
    );
  }

  if (isProcessing) {
    return (
      <span {...containerProps}>
        {children}
      </span>
    );
  }

  return (
    <span 
      {...containerProps}
      dangerouslySetInnerHTML={{ __html: processedContent }}
    />
  );
};

export default MathText;